import { UseQueryOptions } from "@tanstack/react-query";
import { IPublicTenantContext } from "../library/models/publicTenantContext";
import { IServiceCatalog } from "../library/models/serviceCatalog";
import { ITenantHome } from "../library/models/tenantHome";
import { ITenantProfile } from "../library/models/tenantProfile";
import { BaseService } from "./baseService";

/**
 * A clinic's front door, for a visitor with no session.
 *
 * Every route here answers for the clinic named by the request's **hostname**, and none of
 * them takes a clinic as a parameter -- there is no `?tenantId=` to pass. A visitor sees the
 * clinic whose address they typed and nothing else, which is a property of the API rather than
 * of this class being careful.
 *
 * These exist because `/about` and `/ourServices` have always been public routes fetching
 * authenticated endpoints: signed out, they got a 401 and drew a blank page. That was
 * survivable while the only address was the platform's, and is not now that a clinic's own
 * domain is meant to be their whole public presence.
 */
export class PublicTenantService extends BaseService {

    /**
     * The first call the app makes, on every page load, signed in or not.
     *
     * Hand-written rather than built with `createGetQuery` for the same reason
     * `TenantBrandingService.getBranding` is: the key has to say which hostname the answer is
     * for. The URL alone would be one cache entry shared by every clinic -- harmless in a
     * browser, which only ever sees one origin, and wrong in a test that renders two.
     */
    getTenantContext(hostname: string): UseQueryOptions<IPublicTenantContext, Error> {
        return {
            queryKey: ['/public/tenant-context', hostname],
            queryFn: (): Promise<IPublicTenantContext> =>
                this.get<IPublicTenantContext>('/public/tenant-context'),
        };
    }

    /**
     * The front page's copy, for whoever is looking at this hostname.
     *
     * The one route here the page calls whether or not anybody is signed in, and that is
     * deliberate: `/home` is decided by the address, and signing in does not change what it
     * says. So this is not the "signed out" half of a pair the way the two below are -- there
     * is no authenticated equivalent the page ever reaches for.
     *
     * A hostname that names no clinic answers 404, which the page reads as "draw the
     * platform's own front page". A clinic that has written nothing answers 200 with nulls,
     * which means "draw the copy we ship" -- see `homeContent.ts`.
     */
    getTenantHome(hostname: string): UseQueryOptions<ITenantHome, Error> {
        return {
            queryKey: ['/public/tenant-home', hostname],
            queryFn: (): Promise<ITenantHome> => this.get<ITenantHome>('/public/tenant-home'),
        };
    }

    /** The About page's copy, for a signed-out visitor. */
    getTenantProfile(hostname: string): UseQueryOptions<ITenantProfile, Error> {
        return {
            queryKey: ['/public/tenant-profile', hostname],
            queryFn: (): Promise<ITenantProfile> => this.get<ITenantProfile>('/public/tenant-profile'),
        };
    }

    /** The price list, for a signed-out visitor. */
    getServiceCatalog(hostname: string): UseQueryOptions<IServiceCatalog, Error> {
        return {
            queryKey: ['/public/services', hostname],
            queryFn: (): Promise<IServiceCatalog> => this.get<IServiceCatalog>('/public/services'),
        };
    }
}
